import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import StarRating from '@/components/StarRating';
import { ThumbsUp, ThumbsDown, CheckCircle, EyeOff, Eye, Shield } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ReviewCard = ({ 
  review, 
  onHide, 
  onUnhide, 
  onVerify, 
  onHelpful,
  showManagement = false
}) => {
  const [voting, setVoting] = useState(false);

  const handleHelpfulVote = async (helpful) => {
    if (voting) return;
    setVoting(true);
    await onHelpful(review.id, helpful);
    setVoting(false);
  };

  return (
    <Card className={`p-4 ${review.is_hidden ? 'opacity-60 border-red-200' : ''}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-stone-900">{review.reviewer_name}</h4>
            {review.is_verified && (
              <div className="flex items-center gap-1 text-green-600 text-xs">
                <CheckCircle size={14} />
                <span>Verified</span>
              </div>
            )}
            {review.is_hidden && showManagement && (
              <div className="flex items-center gap-1 text-red-600 text-xs">
                <EyeOff size={14} />
                <span>Hidden</span>
              </div>
            )}
          </div>
          <StarRating rating={review.rating} size={16} showNumber={false} />
        </div>
        <span className="text-xs text-stone-500">
          {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
        </span>
      </div>

      {review.service_type && (
        <div className="text-sm text-stone-600 mb-2">
          Service: <span className="font-medium">{review.service_type}</span>
        </div>
      )}

      <p className="text-stone-700 mb-3 whitespace-pre-wrap">{review.review_text}</p>

      {review.date_of_service && (
        <p className="text-xs text-stone-500 mb-3">
          Service Date: {new Date(review.date_of_service).toLocaleDateString()}
        </p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-stone-200">
        {/* Helpful votes */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleHelpfulVote(true)}
            disabled={voting}
            className="flex items-center gap-1 text-sm text-stone-600 hover:text-green-600 transition-colors"
          >
            <ThumbsUp size={16} />
            <span>{review.helpful_count || 0}</span>
          </button>
          <button
            onClick={() => handleHelpfulVote(false)}
            disabled={voting}
            className="flex items-center gap-1 text-sm text-stone-600 hover:text-red-600 transition-colors"
          >
            <ThumbsDown size={16} />
            <span>{review.not_helpful_count || 0}</span>
          </button>
        </div>

        {/* Management buttons */}
        {showManagement && (
          <div className="flex gap-2">
            {!review.is_verified && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onVerify(review.id)}
                className="text-green-600 hover:text-green-700"
              >
                <Shield size={14} className="mr-1" />
                Verify
              </Button>
            )}
            {review.is_hidden ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUnhide(review.id)}
                className="text-blue-600 hover:text-blue-700"
              >
                <Eye size={14} className="mr-1" />
                Unhide
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onHide(review.id)}
                className="text-red-600 hover:text-red-700"
              >
                <EyeOff size={14} className="mr-1" />
                Hide
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ReviewCard;
